import {
  memo,
  useState,
  useMemo,
} from 'react';
import { Input } from 'antd';
import { ArrowLeftOutlined, SearchOutlined } from '@ant-design/icons';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

import IntegrationIcon from '../../integrations/IntegrationIcon';

import styles from './NodeActionModal.module.scss';

type NodeActionModalProps = {
  onBack: () => void;
  connection: Connection | null;
  nodeActions: NodeAction[];
  selectedActionUuid?: string;
  onActionSelect: (actionUuid: string) => void;
};

const NodeActionModal = ({
  onBack,
  connection,
  nodeActions,
  selectedActionUuid,
  onActionSelect,
}: NodeActionModalProps) => {
  const { _ } = useLingui();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter actions based on search query
  const filteredActions = useMemo(() => {
    if (!nodeActions) return [];
    if (!searchQuery.trim()) return nodeActions;
    
    const query = searchQuery.toLowerCase();
    return nodeActions.filter((action: NodeAction) => 
      action.name.toLowerCase().includes(query)
    );
  }, [nodeActions, searchQuery]);

  const handleActionClick = (actionUuid: string) => {
    onActionSelect(actionUuid);
  };

  return (
    <div className={styles.container}>
      {/* Header with Back Button */}
      <div className={styles.header}>
        <button
          type="button"
          onClick={onBack}
          className={styles.backButton}
        >
          <ArrowLeftOutlined />
          <span className={styles.backText}>
            <Trans>BACK</Trans>
          </span>
        </button>
      </div>

      {/* Connection Info */}
      {connection && (
        <div className={styles.connectionInfo}>
          <div className={styles.connectionIconContainer}>
            <IntegrationIcon
              iconUrl={connection.nodetype_icon}
              slug={connection.nodetype_slug}
              name={connection.nodetype_name || ''}
              imageClassName={styles.connectionIcon}
            />
          </div>
          <div className={styles.connectionDetails}>
            <div className={styles.connectionName}>{connection.name}</div>
          </div>
        </div>
      )}

      {/* Actions Section */}
      <div className={styles.actionsSection}>
        <div className={styles.actionsTitle}>
          <Trans>Select action</Trans>
        </div>

        {/* Actions List */}
        <div className={styles.actionsList}>
          {filteredActions?.map((action: NodeAction) => (
            <div
              key={action.uuid}
              className={`${styles.actionItem} ${
                action.uuid === selectedActionUuid ? styles.actionItemSelected : ''
              }`}
              onClick={() => handleActionClick(action.uuid)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActionClick(action.uuid);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className={styles.actionIconContainer}>
                <IntegrationIcon
                  iconUrl={connection?.nodetype_icon}
                  slug={connection?.nodetype_slug}
                  name={action.name}
                  imageClassName={styles.actionIcon}
                />
              </div>
              <div className={styles.actionInfo}>
                <div className={styles.actionName}>{action.name}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Search Input at Bottom - Fixed Position */}
        <div className={styles.searchContainer}>
          <Input
            placeholder={_(msg`Search modules`)}
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            allowClear
          />
        </div>
      </div>
    </div>
  );
};

const MemoizedNodeActionModal = memo(NodeActionModal);
export default MemoizedNodeActionModal;
